import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Skeleton from "@mui/material/Skeleton";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Mail, Search, UserPlus } from "lucide-react";
import AccessModal from "../Access_modal";
import { toast } from "react-toastify";
import { useLazySearchRegisteredUserByEmailQuery } from "../../../../features/user/userApiSlice";
import { useSaveShopMemberAccessMutation } from "../../../../features/shop/shopMemberApiSlice";
import { decodeId } from "../../../../utils/idObfuscator";
import RegisteredUserAvatar from "../RegisteredUserAvatar";
import "./style.css";

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const ModalEmail = ({
  handleClose,
  show,
  shopId,
}) => {
  const resolvedShopId = decodeId(shopId) || shopId || "";
  const [email, setEmail] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [accessUser, setAccessUser] = useState(null);
  const [openAccessAfterExit, setOpenAccessAfterExit] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [membershipId, setMembershipId] = useState("");
  const [isSavingAccess, setIsSavingAccess] = useState(false);
  const [accessError, setAccessError] = useState("");
  const [
    searchRegisteredUser,
    { currentData, originalArgs, isFetching, isError, error, reset },
  ] = useLazySearchRegisteredUserByEmailQuery();
  const [saveShopMemberAccess] = useSaveShopMemberAccessMutation();

  useEffect(() => {
    if (!show || !isValidEmail(email)) {
      setIsDebouncing(false);
      return undefined;
    }

    setIsDebouncing(true);
    const timeoutId = window.setTimeout(() => {
      setIsDebouncing(false);
      searchRegisteredUser(
        {
          email: email.trim(),
          shopId: resolvedShopId,
        },
        false,
      );
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [email, resolvedShopId, searchRegisteredUser, show]);

  const transitioningRef = React.useRef(false);

  useEffect(() => {
    if (
      !show &&
      !transitioningRef.current &&
      !openAccessAfterExit &&
      !showAccess
    ) {
      setEmail("");
      setSelectedUser(null);
      setIsDebouncing(false);
      setAccessUser(null);
      if (typeof reset === "function") reset();
    }
  }, [show, openAccessAfterExit, showAccess, reset]);

  const handleSearch = async (event) => {
    event.preventDefault();
    const userToUse =
      selectedUser || (matchingUser && !cannotInvite ? matchingUser : null);

    if (
      !userToUse ||
      !resolvedShopId ||
      userToUse?.isOwner ||
      userToUse?.invitationStatus === "awaiting_acceptance"
    )
      return;

    if (!selectedUser) {
      setSelectedUser(userToUse);
    }
    setIsSavingAccess(true);
    setAccessError("");
    try {
      const response = await saveShopMemberAccess({
        shopId: resolvedShopId,
        userId: userToUse._id,
        role: "admin",
        accessAreas: [
          "business-dashboard",
          "messanger",
          "shop-reports",
          "post-edit-items",
        ],
      }).unwrap();

      if (response?.data?.alreadyActive) {
        toast.success(
          response?.message || "You have access based on shop access",
          { position: "top-center" },
        );
        transitioningRef.current = false;
        setOpenAccessAfterExit(false);
        handleClose();
        return;
      }

      setMembershipId(response.data.membershipId);
      setAccessUser(selectedUser);
      transitioningRef.current = true;
      setOpenAccessAfterExit(true);
      handleClose();
      window.setTimeout(() => {
        if (transitioningRef.current) {
          transitioningRef.current = false;
          setOpenAccessAfterExit(false);
          setShowAccess(true);
        }
      }, 300);
    } catch (error) {
      setAccessError(
        error?.data?.message || "Unable to prepare access. Please try again.",
      );
    } finally {
      setIsSavingAccess(false);
    }
  };

  const handleSelectUser = (user) => {
    if (
      user?.isOwner ||
      user?.invitationStatus === "awaiting_acceptance"
    )
      return;
    setSelectedUser(user);
    setEmail("");
    setIsDebouncing(false);
  };

  const handleInviteModalExited = () => {
    if (transitioningRef.current || openAccessAfterExit) {
      transitioningRef.current = false;
      setOpenAccessAfterExit(false);
      setShowAccess(true);
    }
  };

  const handleGrantAccess = () => {
    if (!accessUser) return;
    transitioningRef.current = false;
    setShowAccess(false);
    setAccessUser(null);
    handleClose();
  };

  const handleCancelInvite = () => {
    transitioningRef.current = false;
    setOpenAccessAfterExit(false);
    setShowAccess(false);
    setAccessUser(null);
    setEmail("");
    setSelectedUser(null);
    setIsDebouncing(false);
    if (typeof reset === "function") reset();
    handleClose();
  };

  const hasSearchTerm = Boolean(email.trim());
  const canInviteEmail = isValidEmail(email);
  const searchedEmail =
    typeof originalArgs === "string"
      ? originalArgs
      : originalArgs?.email || "";
  const searchMatchesInput =
    searchedEmail.toLowerCase() === email.trim().toLowerCase();
  const foundUser = searchMatchesInput ? currentData?.data?.user : null;
  const matchingUser =
    foundUser?.email?.toLowerCase() === email.trim().toLowerCase()
      ? foundUser
      : null;
  const isAwaitingAcceptance =
    matchingUser?.invitationStatus === "awaiting_acceptance";
  const isActiveMember = matchingUser?.invitationStatus === "active";
  const isOwner = Boolean(matchingUser?.isOwner);
  const cannotInvite = isAwaitingAcceptance || isOwner;
  const noUserFound =
    searchMatchesInput &&
    isError &&
    (error?.status === 404 || error?.data?.message === "No user found");

  return (
    <>
      <AccessModal
        showaccess={showAccess}
        handleCloseaccess={() => {
          setShowAccess(false);
          setAccessUser(null);
          setOpenAccessAfterExit(false);
        }}
        email={accessUser?.email}
        onGrantAccess={handleGrantAccess}
        shopId={resolvedShopId}
        membershipId={membershipId}
      />
      <Modal
        className="shop-invite-modal"
        centered
        show={show}
        onHide={handleCancelInvite}
        onExited={handleInviteModalExited}
        aria-labelledby="shop-invite-title"
      >
        <Modal.Header closeButton className="shop-invite-header">
          <div className="shop-invite-heading">
            <span className="shop-invite-heading-icon">
              <UserPlus size={19} />
            </span>
            <div>
              <span className="shop-invite-step-badge">Step 1 of 2</span>
              <Modal.Title id="shop-invite-title">Add a shop admin</Modal.Title>
              <p>Invite someone by email to help manage your shop.</p>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="shop-invite-body">
          <Form noValidate onSubmit={handleSearch}>
            <Form.Label htmlFor="shop-admin-email">Email address</Form.Label>
            <div className="shop-invite-search">
              <Search className="shop-invite-search-icon" size={17} />
              <Form.Control
                id="shop-admin-email"
                type="email"
                value={email}
                autoComplete="email"
                placeholder="name@example.com"
                aria-label="Email address"
                aria-controls="shop-invite-results"
                aria-expanded={hasSearchTerm}
                onChange={(event) => {
                  const value = event.target.value;
                  setEmail(value);
                  setSelectedUser(null);
                  setIsDebouncing(isValidEmail(value));
                  if (!value.trim() && typeof reset === "function") {
                    reset();
                  }
                }}
              />
              {email && (
                <button
                  className="shop-invite-clear"
                  type="button"
                  aria-label="Clear email"
                  onClick={() => {
                    setEmail("");
                    setSelectedUser(null);
                    setIsDebouncing(false);
                    if (typeof reset === "function") reset();
                  }}
                >
                  ×
                </button>
              )}
            </div>
            <div className="shop-invite-hint">
              Search by email to prepare an invitation.
            </div>
            {accessError && (
              <div className="shop-invite-invalid" role="alert">
                {accessError}
              </div>
            )}

            {selectedUser && (
              <div className="shop-invite-selected-user">
                <RegisteredUserAvatar user={selectedUser} />
                <span className="shop-invite-result-copy">
                  <strong>{selectedUser.fullname || selectedUser.email}</strong>
                  <small>{selectedUser.email}</small>
                </span>
                <Check
                  className="shop-invite-result-check"
                  size={17}
                  aria-label="Selected"
                />
              </div>
            )}

            <AnimatePresence initial={false}>
              {hasSearchTerm && (
                <motion.div
                  id="shop-invite-results"
                  className="shop-invite-results"
                  role="listbox"
                  aria-label="Email invitation"
                  initial={{ opacity: 0, height: 0, y: -4 }}
                  animate={{ opacity: 1, height: "auto", y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -4 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                >
                  {isDebouncing || isFetching ? (
                    <div
                      className="shop-invite-skeleton"
                      aria-label="Searching"
                    >
                      <Skeleton variant="circular" width={34} height={34} />
                      <div>
                        <Skeleton variant="text" width={158} height={17} />
                        <Skeleton variant="text" width={112} height={14} />
                      </div>
                    </div>
                  ) : matchingUser ? (
                    <button
                      type="button"
                      className={`shop-invite-result ${
                        selectedUser?._id === matchingUser._id
                          ? "is-selected"
                          : ""
                      } ${cannotInvite ? "is-disabled" : ""}`}
                      role="option"
                      aria-selected={selectedUser?._id === matchingUser._id}
                      aria-disabled={cannotInvite}
                      disabled={cannotInvite}
                      onClick={() => {
                        if (!cannotInvite) {
                          handleSelectUser(matchingUser);
                        }
                      }}
                    >
                      <RegisteredUserAvatar user={matchingUser} />
                      <span className="shop-invite-result-copy">
                        <strong>
                          {matchingUser.fullname || matchingUser.email}
                        </strong>
                        <small>{matchingUser.email}</small>
                      </span>
                      {isOwner ? (
                        <span className="shop-invite-badge-owner">
                          Shop owner
                        </span>
                      ) : isAwaitingAcceptance ? (
                        <span className="shop-invite-badge-invited">
                          Already invited
                        </span>
                      ) : isActiveMember ? (
                        <span className="shop-invite-badge-invited" style={{ backgroundColor: "#e0f2fe", color: "#0369a1", borderColor: "#bae6fd" }}>
                          Active member
                        </span>
                      ) : selectedUser?._id === matchingUser._id ? (
                        <Check className="shop-invite-result-check" size={17} />
                      ) : (
                        <ArrowRight
                          className="shop-invite-result-arrow"
                          size={17}
                        />
                      )}
                    </button>
                  ) : noUserFound ? (
                    <div className="shop-invite-invalid" role="status">
                      No user found
                    </div>
                  ) : searchMatchesInput && isError ? (
                    <div className="shop-invite-invalid" role="alert">
                      {error?.data?.message ||
                        "Unable to search users. Try again."}
                    </div>
                  ) : !canInviteEmail ? (
                    <div className="shop-invite-invalid" role="status">
                      Enter a valid email address to continue.
                    </div>
                  ) : null}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="shop-invite-actions">
              <button
                type="button"
                className="shop-invite-cancel"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="shop-invite-next"
                disabled={
                  !selectedUser ||
                  isSavingAccess ||
                  !resolvedShopId ||
                  selectedUser?.isInvited ||
                  selectedUser?.isOwner
                }
              >
                {isSavingAccess ? "Saving..." : "Continue"}
                <ArrowRight size={16} />
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ModalEmail;
